import React, { useState, useEffect } from 'react';
import { PatientProfile, UpdateProfileData } from '@/hooks/use-profile'; // Use existing types
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ProfileFormProps {
  profileData: PatientProfile | null; 
  onSubmit: (data: UpdateProfileData) => Promise<void>; // Expects snake_case data
  isSubmitting: boolean;
}

// Revert to simpler state management
const ProfileForm: React.FC<ProfileFormProps> = ({ profileData, onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState<UpdateProfileData>({
    // Initialize with snake_case keys, matching UpdateProfileData
    first_name: '',
    last_name: '',
    birth_date: '', // Expects YYYY-MM-DD
    gender: 'prefer-not-to-say',
    email: '',
    phone: '',
    address: '', // Assuming address is a simple string for now based on previous state
    // Add other fields from UpdateProfileData if necessary
  });

  // Update local state when profileData loads or changes
  useEffect(() => {
    if (profileData) {
      setFormData({
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        birth_date: profileData.birth_date?.split('T')[0] || '', // Format date
        gender: profileData.gender || 'prefer-not-to-say',
        email: profileData.email || '',
        phone: profileData.phone || '',
        address: profileData.address || '', // Assign simple address string
      });
    }
  }, [profileData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSelectChange = (name: keyof UpdateProfileData) => (value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData); // Pass the snake_case formData
  };

  return (
    <form onSubmit={handleFormSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label htmlFor="first_name" className="block text-sm font-medium">First Name</label>
          <Input id="first_name" name="first_name" value={formData.first_name || ''} onChange={handleChange} required />
        </div>
        <div className="space-y-1">
          <label htmlFor="last_name" className="block text-sm font-medium">Last Name</label>
          <Input id="last_name" name="last_name" value={formData.last_name || ''} onChange={handleChange} required />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label htmlFor="birth_date" className="block text-sm font-medium">Date of Birth</label>
          <Input id="birth_date" name="birth_date" type="date" value={formData.birth_date || ''} onChange={handleChange} required />
        </div>
         <div className="space-y-1">
            <label htmlFor="gender" className="block text-sm font-medium">Gender</label>
            <Select value={formData.gender} onValueChange={handleSelectChange('gender')} >
              <SelectTrigger aria-label="Gender">
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
                <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
          </div>
      </div>
      
      <div className="space-y-1">
        <label htmlFor="email" className="block text-sm font-medium">Email Address</label>
        <Input id="email" name="email" type="email" placeholder="your.email@example.com" value={formData.email || ''} onChange={handleChange} required />
      </div>

      <div className="space-y-1">
        <label htmlFor="phone" className="block text-sm font-medium">Phone Number</label>
        <Input id="phone" name="phone" type="tel" placeholder="(123) 456-7890" value={formData.phone || ''} onChange={handleChange} />
      </div>
      
      <div className="space-y-1">
        <label htmlFor="address" className="block text-sm font-medium">Address (Optional)</label>
        <Input id="address" name="address" placeholder="123 Main St, City, State Zip" value={formData.address || ''} onChange={handleChange} />
      </div>

      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Saving...' : 'Save Profile'}
      </Button>
    </form>
  );
};

export default ProfileForm; 